/**
 * injectTextSizes - Inyecta tamaños de texto aumentados para el widget
 *
 * Este módulo permite ajustar los tamaños de texto según la configuración
 * de font-size: small (+0px), default (+1px), large (+2px)
 *
 * La escala sale por DOS vías, con los mismos valores (una sola fuente de verdad):
 *   - reglas `#hm-popup .text-*` (histórico), para todo lo que lleva clase Tailwind;
 *   - custom properties `--hm-fs-*` en `:host` (ver CSS_VAR_TOKENS), para lo que no
 *     puede llevarla: estilos inline, el globo del teaser (fuera de #hm-popup) y la
 *     pastilla del shell pre-React.
 *
 * @module injectTextSizes
 */

interface TextSizeConfig {
  increment: number;
}

const SIZE_CONFIG: Record<string, TextSizeConfig> = {
  'small': { increment: 0 },
  'default': { increment: 1 },
  'large': { increment: 2 },
};

/**
 * Genera el mapa de tamaños de texto con el incremento aplicado
 */
function generateTextSizeMap( increment: number ): Record<string, string> {
  return {
    'text-xs': `${12 + increment}px`,
    'text-sm': `${14 + increment}px`,
    'text-base': `${16 + increment}px`,
    'text-lg': `${18 + increment}px`,
    'text-xl': `${20 + increment}px`,
    'text-2xl': `${24 + increment}px`,
    'text-3xl': `${30 + increment}px`,
    'text-4xl': `${36 + increment}px`,
    'text-5xl': `${48 + increment}px`,
    'text-6xl': `${60 + increment}px`,
    'text-7xl': `${72 + increment}px`,
    'text-8xl': `${96 + increment}px`,
    'text-9xl': `${128 + increment}px`,
  };
}

/**
 * Mapa de tamaños para MÓVIL (viewport <= 480px) — feedback cliente TCT 11-ago-2026.
 *
 * En móvil el chat se leía a ~12-14px mientras el texto de lectura del sitio anfitrión
 * ronda los 18-20px (medido en tacticalcenter.es a 390px: body 18px, p 20px). Este mapa
 * sube las clases de LECTURA (xs/sm/base/lg/xl) hasta el suelo de mensajería móvil
 * (16-17px, el rango de WhatsApp/iMessage) sin tocar los display sizes (2xl+), que ya
 * son grandes. El interlineado acompaña solo (las clases leading-* son relativas).
 */
function generateMobileTextSizeMap( increment: number ): Record<string, string> {
  return {
    'text-xs': `${13 + increment}px`,
    'text-sm': `${16 + increment}px`,
    'text-base': `${17 + increment}px`,
    'text-lg': `${19 + increment}px`,
    'text-xl': `${21 + increment}px`,
  };
}

/**
 * Marca del bloque de estilos. La MISMA que usa syncPendingStyles (widget/index.tsx,
 * REPLACEABLE_STYLE_BLOCKS) para tratar los tamaños como ESTADO y no como acumulación.
 */
const STYLE_BLOCK_MARK = '/* Hellomatik - Text sizes */';

/**
 * Tokens de la escala que ADEMÁS se publican como custom properties.
 *
 * Las reglas `#hm-popup .text-*` solo alcanzan a lo que sea descendiente del popup Y
 * lleve la clase. Se quedaban fuera tres superficies reales del widget:
 *   1. la ficha de producto (ProductOverlay), que va TODA en `style={{...}}` porque el
 *      widget usa Tailwind precompilado y una clase nueva no aplicaría;
 *   2. el globo del teaser, que vive fuera de `#hm-popup` (overflow:hidden);
 *   3. la pastilla del shell pre-React (bootstrap.ts), que ni existe en el árbol React.
 * Con las vars, esos tres consumen la MISMA escala en vez de llevar px sueltos que en
 * móvil se quedaban en 11-14px (feedback cliente TCT). Se emiten en `:host`: el shell y
 * `#hm-popup` viven en el MISMO shadow root, así que ambos las heredan de ahí.
 *
 * Solo los tamaños de LECTURA (xs…xl), que son los que el mapa móvil sube; los display
 * (2xl+) no tienen var porque no tienen versión móvil que sincronizar.
 */
const CSS_VAR_TOKENS: Record<string, string> = {
  'text-xs': '--hm-fs-xs',
  'text-sm': '--hm-fs-sm',
  'text-base': '--hm-fs-base',
  'text-lg': '--hm-fs-lg',
  'text-xl': '--hm-fs-xl',
};

/**
 * Bloque `:host` con las custom properties de un mapa de tamaños
 *
 * Sin `!important`: no hay defaults de estas vars en ningún otro sitio (a diferencia de
 * los colores, que sí los tienen en index.css), y los consumidores son estilos inline,
 * que ganan siempre. La versión móvil gana por ORDEN: va dentro del @media, que se emite
 * después del bloque de escritorio en la misma hoja.
 *
 * @param sizeMap - Mapa clase → tamaño (escritorio o móvil)
 * @returns {string} Bloque CSS `:host { ... }`
 */
function generateHostVarsBlock( sizeMap: Record<string, string> ): string {
  let rules = ':host {\n';

  Object.entries( CSS_VAR_TOKENS ).forEach( ( [className, cssVarName] ) => {
    const size = sizeMap[className];
    if( size ) rules += `  ${cssVarName}: ${size};\n`;
  } );

  return rules + '}\n';
}

/**
 * Inyecta los tamaños de texto aumentados en el DOM
 *
 * Lee la configuración de font-size desde chat-colors y aplica el incremento
 * correspondiente: small (+0px), default (+1px), large (+2px)
 * 
 * NOTA: Los estilos se guardan en __HELLOMATIK_PENDING_STYLES__ para que el widget
 * los inyecte dentro del Shadow DOM cuando se monte.
 *
 * @example
 * injectTextSizes();
 *
 * @returns {void}
 */
export function injectTextSizes(): void {
  try {
    // Obtener la configuración de font-size desde chat-colors
    const config      = (window as any).HELLOMATIK_CONFIG || {};
    const chatColors  = config['chat-colors'] || {};
    const fontSize    = chatColors['font-size'] || 'default';

    // Obtener el incremento según la configuración
    const sizeConfig  = SIZE_CONFIG[fontSize] || SIZE_CONFIG['default'];
    const increment   = sizeConfig.increment;

    // Generar el mapa de tamaños con el incremento
    const textSizeMap = generateTextSizeMap( increment );

    // Construir el CSS con todas las clases de texto aumentadas
    let cssRules = `${STYLE_BLOCK_MARK}\n`;

    Object.entries( textSizeMap ).forEach( ( [className, fontSize] ) => {
      // Aplicar reglas SOLO dentro de #hm-popup
      cssRules += `#hm-popup .${className}:not(#ai-input-04) { font-size: ${fontSize} !important; }\n`;
    } );

    // Las MISMAS medidas como custom properties, para lo que no puede llevar clase
    // (ver CSS_VAR_TOKENS). Aditivo: las reglas de clase de arriba no se tocan.
    cssRules += generateHostVarsBlock( textSizeMap );

    // MÓVIL (feedback cliente TCT 11-ago-2026): tamaños de lectura al nivel del texto del
    // sitio anfitrión (ver generateMobileTextSizeMap). El composer va aparte y a 16px
    // exactos: por debajo de 16px iOS hace auto-zoom al enfocar el input, que era parte
    // de la sensación de "letra diminuta" en iPhone.
    const mobileTextSizeMap = generateMobileTextSizeMap( increment );

    cssRules += '@media (max-width: 480px) {\n';
    Object.entries( mobileTextSizeMap ).forEach( ( [className, mobileSize] ) => {
      cssRules += `#hm-popup .${className}:not(#ai-input-04) { font-size: ${mobileSize} !important; }\n`;
    } );
    cssRules += '#hm-popup #ai-input-04 { font-size: 16px !important; }\n';
    cssRules += generateHostVarsBlock( mobileTextSizeMap );
    cssRules += '}\n';

    // Guardar en variable global para que el widget lo inyecte en el Shadow DOM
    if( !( window as any ).__HELLOMATIK_PENDING_STYLES__ )
      ( window as any ).__HELLOMATIK_PENDING_STYLES__ = [];

    // IDEMPOTENTE: los tamaños son ESTADO (el último gana), así que se REEMPLAZA el
    // bloque que ya hubiera en la cola en vez de apilar otro. Antes cada llamada
    // (refresco de config, remount, vista previa del panel) empujaba un bloque más:
    // el consumidor del widget ya los compactaba, pero el del shell pre-React los
    // concatena TODOS en un solo <style> y la cola crecía sin techo mientras el
    // widget no se hubiera montado.
    const pendingStyles: string[] = ( window as any ).__HELLOMATIK_PENDING_STYLES__;
    const existingIndex = pendingStyles.findIndex(
      css => typeof css === 'string' && css.startsWith( STYLE_BLOCK_MARK )
    );

    if( existingIndex >= 0 ) pendingStyles[existingIndex] = cssRules;
    else                     pendingStyles.push( cssRules );

  } catch( error ) {
    if( import.meta.env.DEV ) console.error( '[Text Sizes] Failed to inject text sizes:', error );
  }
}

/**
 * Resetea los tamaños de texto a los valores por defecto
 * Útil para pruebas o cuando se quiere volver a los tamaños originales
 *
 * @returns {void}
 */
export function resetTextSizes(): void {
  const styleElement = document.getElementById( 'hellomatik-text-sizes' );

  if( styleElement ) {
    styleElement.remove();
  }

  ( window as any ).HELLOMATIK_INCREASE_TEXT_SIZE = false;
}
